import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AnimatedSection } from '../ui/AnimatedSection';
import { useTenant } from '../../context/TenantContext';
import { inlineEm } from './inlineEm';

const DEFAULTS = {
  headline: 'Find your *next* twelve weeks.',
  body: 'Browse the catalogue, sit in on an intro session, see how it feels.',
  buttonText: 'Browse courses',
};

export const CTASection: React.FC = () => {
  const { tenant } = useTenant();
  const cta = tenant?.branding.marketing?.cta ?? {};
  const headline = cta.headline || DEFAULTS.headline;
  const body = cta.body || DEFAULTS.body;
  const buttonText = cta.buttonText || DEFAULTS.buttonText;
  const eyebrow = cta.eyebrow?.trim();

  return (
    <section id="cta" className="relative bg-gray-900 text-white overflow-hidden">
      {/* Single soft accent glow — replaces the previous double-blob decoration */}
      <div
        className="absolute -top-1/2 right-[-10%] w-[70%] h-[200%] opacity-20 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, var(--color-primary-500, #ef4444) 0%, transparent 60%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <AnimatedSection className="grid md:grid-cols-[1fr_auto] gap-10 md:gap-16 items-end">
          <div>
            {eyebrow && (
              <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-400 mb-6 flex items-center gap-3.5">
                <span className="text-primary-300">→</span>
                <span>{eyebrow}</span>
              </div>
            )}
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.025em] max-w-[16ch] text-balance">
              {inlineEm(headline)}
            </h2>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.12em] text-gray-400 max-w-[60ch]">
              {body}
            </p>
          </div>

          <Link
            to="/courses"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-white text-gray-900 text-base font-semibold rounded-full hover:bg-primary-50 transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap self-start md:self-end"
          >
            {buttonText}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
};
